import type { NextPage, GetServerSideProps } from "next";

import { ServerSidePageLogic } from "@/backend/logic";
import { CacheByKeyService } from "@/backend/service/cache";
import { ProfileService } from "@/backend/service/profile";

import { ErrorBoundary } from "@/src/component/partial/ErrorBoundary";
import { SEO } from "@/src/component/partial/SEO";
import { DynamicPage } from "@/src/component/view/DynamicPage";
import { ProviderHooks } from "@/src/hook";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiSingleType, StrapiCollectionType, BrokerProfileType } from "@/src/type";
import { NextButtonPosition } from "@/src/type/strapi/collection";
import { StringHelper, URLHelper, UserAgentHelper } from "@/src/util";

import theme from "@/styles/scss/theme.module.scss";

export function getHeaderInfoForCurrentSection(page?: StrapiCollectionType.DynamicPage, activeIndex?: number) {
  if (!page || typeof activeIndex !== "number") {
    return null;
  }
  const currentSection = page?.dynamic_page_sections?.data?.[activeIndex ?? 0];
  if (!currentSection) {
    return null;
  }

  const header = currentSection?.attributes?.header;
  const backgroundColor = CMSLogic.colorSelector(currentSection?.attributes?.background?.color) ?? "transparent";
  return {
    header,
    backgroundColor,
  };
}

export function getCurrentSection(searchParams: any, page: StrapiCollectionType.DynamicPage): number {
  try {
    const sectionTag = StringHelper.checkIsStringOrIsNull(searchParams?.["section"], "false");
    if (sectionTag !== "false") {
      const section = page?.dynamic_page_sections?.data?.find((section) => section.attributes?.slug === sectionTag);
      if (section) {
        const index = page?.dynamic_page_sections?.data?.indexOf(section);
        if (index >= 0) {
          return index;
        }
      }
    }
    return parseInt(StringHelper.checkIsStringOrIsNull(searchParams?.["active"], "0"), 10);
  } catch (err) {
    return 0;
  }
}

export function getDomainData(hostname: string, page: StrapiCollectionType.DynamicPage): StrapiCollectionType.Domain | undefined {
  if (!hostname || !page) {
    return;
  }
  return (page.domains?.data ?? []).find((d) => d.attributes?.hostname === hostname)?.attributes;
}

type NextIconType = {
  nextButtonText?: string | null;
  nextIconColor: "light" | "dark";
  nextButtonPosition?: NextButtonPosition | null;
};

export function getCurrentNextIcon(page: StrapiCollectionType.DynamicPage, currentSection: number): NextIconType {
  const nextSection = page?.dynamic_page_sections?.data?.[currentSection ?? 0];
  return {
    nextButtonText: nextSection?.attributes?.nextButtonText ?? null,
    nextIconColor: nextSection?.attributes?.nextIconColor ?? "light",
    nextButtonPosition: nextSection?.attributes?.nextButtonPosition ?? page?.nextButtonPosition ?? null,
  };
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");

  const { hostname, byPassCache, isPreview, returnHeaders, trackingOff, locale, isToolkit } = ServerSidePageLogic.getAllParams(context);
  const paths = context.params?.paths;

  if (Array.isArray(paths)) {
    const slug = paths.join("/");

    //transform data for the page
    const cacheResponse = await CacheByKeyService.cacheAllRequests(slug, hostname, locale, byPassCache);
    if (!cacheResponse?.page || !cacheResponse?.webConfig) {
      return {
        redirect: {
          permanent: false,
          destination: "/missing",
        },
      };
    }

    //transform the data for the page
    const galleries = cacheResponse.galleries;
    const webConfig = cacheResponse.webConfig;
    const page = cacheResponse.page;
    const domainData = getDomainData(hostname, page);
    const tracking = { ...domainData?.tracking, ...page?.property?.data?.attributes?.tracking };
    const searchParams = context.query ?? {};
    const profileSlug: string | null = StringHelper.checkIsStringOrArrayAndIsNull(context.query?.["profile"], "");
    const profileResponse = profileSlug ? await ProfileService.getBySlug(profileSlug) : null;
    const currentSection = getCurrentSection(searchParams, page);
    const headerInfo = getHeaderInfoForCurrentSection(page, currentSection) ?? {
      header: null,
      backgroundColor: null,
    };

    if (page?.layout === "alternate" && !page?.headerObserver) {
      headerInfo["backgroundColor"] = theme.secondaryMain;
      headerInfo["header"] = ProviderHooks.STATIC_PAGE_HEADER;
    }

    if (page) {
      return {
        props: {
          page,
          webConfig,
          marketingSources: cacheResponse.marketing ?? null,
          locale: isPreview ? "en" : locale,
          layout: page.layout,
          query: URLHelper.serializeParseUrlQuery(context.query),
          searchParams,
          ...headerInfo,
          currentSection,
          profile: profileResponse?.[0] ?? null,
          refreshId: page.slug,
          noAuthRequired: true,
          trackingEnabled: !trackingOff,
          mobileHint: UserAgentHelper.isMobile(context.req?.headers?.["user-agent"]),
          deviceWidthHint: UserAgentHelper.deviceWidthHint(context.req?.headers?.["user-agent"]),
          appleOrMobileHint: UserAgentHelper.appleOrMobile(context.req?.headers?.["user-agent"]),
          domain: hostname,
          headers: returnHeaders ? (context.req?.headers ?? {}) : {},
          nav: cacheResponse.nav ?? null,
          tracking: tracking ?? null,
          domainData: domainData ?? null,
          ...getCurrentNextIcon(page, currentSection),
          isToolkit,
          galleries: galleries ?? null,
        },
      };
    }
  }

  return {
    redirect: {
      permanent: false,
      destination: "/missing",
    },
  };
};

interface DynamicPageProps {
  page: StrapiCollectionType.DynamicPage;
  pathOverride?: string;
  webConfig: StrapiSingleType.WebConfig;
  locale: string;
  searchParams?: Record<string, string | string[] | undefined>;
  profile?: BrokerProfileType.BrokerProfile | null;
  refreshId: string;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  appleOrMobileHint?: boolean;
  domain: string;
  domainData: StrapiCollectionType.Domain | null;
  headers: any;
  nextButtonText?: string;
  nextIconColor: "light" | "dark";
  nextButtonPosition?: NextButtonPosition;
  currentSection: number;
  propertySlug?: string;
}

const DynamicPageSSR: NextPage<DynamicPageProps> = ({
  page,
  pathOverride,
  webConfig,
  searchParams,
  profile,
  refreshId,
  mobileHint,
  deviceWidthHint,
  appleOrMobileHint,
  domain,
  headers,
  domainData,
  nextButtonText,
  nextIconColor,
  nextButtonPosition,
  currentSection,
  propertySlug,
}) => {
  if (!page?.slug) {
    return (
      <ErrorBoundary forceFallback={true}>
        <></>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SEO
        title={page.seo?.title}
        byName={domainData?.siteName}
        pageDescription={page.seo?.description}
        description={domainData?.siteDescription ?? webConfig.description}
        imageURL={page.seo?.image?.data?.attributes?.url ?? domainData?.siteDefaultImage?.data?.attributes?.url}
        favIcon={domainData?.favIcon?.data?.attributes?.url}
        webConfig={webConfig}
        path={page?.slug}
        keywords={CMSLogic.tagsToStringArray(page.seo?.keywords)}
        domain={domain}
        schemaOverride={page.seo?.schemaOverride}
        property={propertySlug ? page.property?.data?.attributes : undefined}
        canonical={page.seo?.canonical}
        pathOverride={pathOverride}
      />
      <DynamicPage
        page={page}
        searchParams={searchParams}
        profile={profile}
        refreshId={refreshId}
        mobileHint={mobileHint}
        deviceWidthHint={deviceWidthHint}
        appleOrMobileHint={appleOrMobileHint}
        domain={domain}
        headers={headers}
        defaultNextButtonText={nextButtonText}
        defaultNextIconColor={nextIconColor}
        defaultNextButtonPosition={nextButtonPosition}
        defaultActiveId={currentSection}
        webConfig={webConfig}
      />
    </ErrorBoundary>
  );
};

export default DynamicPageSSR;
